/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { createElement } from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  type ImageStyle,
  type ViewStyle,
} from 'react-native';
import { getVueltoVMarkUri, VUELTO_V_MARK } from '../../../bootSplashAssets';
import { colors } from '../../shared/theme/colors';
import { ticketColors } from '../../shared/theme/ticketColors';

type BrandWatermarkProps = {
  variant?: 'boot' | 'auth';
};

/** Más ancho que el card auth (420px) para que la V asome en los márgenes. */
const AUTH_MARK_RATIO = 0.72;
const AUTH_MARK_MAX = 340;
const BOOT_MARK_RATIO = 0.72;
const BOOT_MARK_MAX = 320;

/**
 * V glass transparente (assets/vuelto-v-mark.png) como atmósfera de marca.
 * Web: `<img>` + fixed layer — RN Web no pinta backgroundImage en View y Image usa z-index:-1.
 */
export function BrandWatermark({ variant = 'auth' }: BrandWatermarkProps) {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isBoot = variant === 'boot';
  const auth = !isBoot;
  const markSize = Math.min(
    windowWidth * (isBoot ? BOOT_MARK_RATIO : AUTH_MARK_RATIO),
    isBoot ? BOOT_MARK_MAX : AUTH_MARK_MAX
  );
  const opacity = isBoot ? 0.08 : 0.12;
  const webMarkSrc = Platform.OS === 'web' ? getVueltoVMarkUri() : null;

  /** Auth: V más arriba para no quedar 100% detrás del card opaco centrado. */
  const authTopOffset = Math.max(windowHeight * 0.08, 40);

  return (
    <View
      style={[
        styles.layer,
        Platform.OS === 'web' ? styles.layerWeb : null,
        isBoot ? styles.layerBoot : styles.layerAuth,
        auth ? { paddingTop: authTopOffset } : null,
      ]}
      pointerEvents="none"
    >
      {Platform.OS === 'web' && auth
        ? createElement('div', {
            'aria-hidden': true,
            style: {
              position: 'absolute',
              inset: 0,
              background:
                `radial-gradient(900px 480px at 50% -8%, ${ticketColors.brandSoft}, transparent 55%)`,
              pointerEvents: 'none',
            },
          })
        : null}
      <View
        style={[
          styles.markWrap,
          {
            width: markSize,
            height: markSize,
            opacity,
            transform: isBoot ? [{ translateY: -48 }] : undefined,
          },
        ]}
      >
        {Platform.OS === 'web' && webMarkSrc
          ? createElement('img', {
              src: webMarkSrc,
              alt: '',
              'aria-hidden': 'true',
              draggable: false,
              style: {
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: `drop-shadow(0 0 40px ${ticketColors.gold})`,
                userSelect: 'none',
                pointerEvents: 'none',
              },
            })
          : (
            <Image
              source={VUELTO_V_MARK}
              style={styles.nativeMark}
              resizeMode="contain"
              fadeDuration={0}
            />
          )}
      </View>
      <View style={[styles.glow, isBoot ? styles.glowBoot : styles.glowAuth]} />
    </View>
  );
}

const styles = StyleSheet.create({
  layer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
  } as ViewStyle,
  layerWeb: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  } as unknown as ViewStyle,
  layerBoot: {
    justifyContent: 'center',
  } as ViewStyle,
  layerAuth: {
    justifyContent: 'flex-start',
  } as ViewStyle,
  markWrap: {
    zIndex: 1,
  } as ViewStyle,
  nativeMark: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  glow: {
    position: 'absolute',
    borderRadius: 9999,
    backgroundColor: colors.accentSoft,
  } as ViewStyle,
  glowBoot: {
    width: 280,
    height: 280,
    left: '50%',
    marginLeft: -140,
    top: '50%',
    marginTop: -188,
  } as ViewStyle,
  glowAuth: {
    width: 300,
    height: 300,
    left: '50%',
    marginLeft: -150,
    top: '14%',
    opacity: 0.85,
  } as ViewStyle,
});
